import { useCallback } from "react";
import { useHttpAdapter } from "./useHttpAdapter";
import { ContentLog } from "../types/dashboard";

export interface ContentStats {
  scheduled: number;
  draft: number;
  published: number;
  processing: number;
  failed: number;
  partial_failure: number;
}

interface ContentStatsResponse {
  status: string;
  data: ContentStats;
}

interface HistoryByStatusResponse {
  status: string;
  data: ContentLog[];
  total_logs: number;
}

const DEFAULT_STATS: ContentStats = {
  scheduled: 0,
  draft: 0,
  published: 0,
  processing: 0,
  failed: 0,
  partial_failure: 0,
};

const useContentStatsApi = () => {
  const { get } = useHttpAdapter();

  const getContentStats = useCallback(async (): Promise<ContentStats> => {
    try {
      const response = await get<ContentStatsResponse>("/content/stats");
      if (response.data?.status === "success" && response.data?.data) {
        return response.data.data;
      }
      return DEFAULT_STATS;
    } catch (e) {
      return DEFAULT_STATS;
    }
  }, [get]);

  const getHistoryByStatus = useCallback(
    async (
      status: string,
      offset: number,
      limit: number
    ): Promise<{ list: ContentLog[]; total_logs: number }> => {
      try {
        const statusUpper = status.toUpperCase();
        const response = await get<HistoryByStatusResponse>(
          `/content/history/status/${statusUpper}?offset=${offset}&limit=${limit}`
        );
        if (response.data?.status === "success" && Array.isArray(response.data?.data)) {
          return {
            list: response.data.data,
            total_logs: response.data.total_logs ?? 0,
          };
        }
        return { list: [], total_logs: 0 };
      } catch (e) {
        return { list: [], total_logs: 0 };
      }
    },
    [get]
  );

  return { getContentStats, getHistoryByStatus };
};

export default useContentStatsApi;
